import _ from 'lodash'
import { getNodeByUUID } from '@ps/utils/game.js'
import actions from './actions'
import gameIframe from '@ps/bridge/iframe/iframe'
import Vue from 'vue'
import { transformXyLogic2Ui } from '@ps/bridge/qc/node'
import { getDescendants } from '@ps/bridge/qc/node/utils'
import nodePool from '@ps/bridge/qc/node-pool'
import {
  isGlobalScene,
  isNormalScene,
  isPlayScene,
  SceneType,
} from '@ps/bridge/qc/scripts/scene-config'
import * as sceneConfigScript from '@ps/bridge/qc/scripts/scene-config'
import { EventBus, Event } from '@ps/utils/event-bus'

const sceneManager = {
  namespaced: true,

  state: {
    sceneList: [], // 场景列表
    currentSceneNode: null,
    sceneThumbnail: {},
    nodesLayout: {},
    isCapturing: false,
    sceneEditType: SceneType.NORMAL, // 场景编辑类型, 枚举值是 SceneType
  },

  getters: {
    playSceneList(state) {
      return state.sceneList.filter((scene) => isPlayScene(scene))
    },
    normalSceneList(state) {
      return state.sceneList.filter((scene) => isNormalScene(scene))
    },
    globalScene(state) {
      return state.sceneList.find((scene) => isGlobalScene(scene))
    },
  },

  mutations: {
    markDirty(state, { orientation, sceneId }) {
      const scene = state.sceneThumbnail[sceneId] || {}
      const storedThumb = scene.thumbnail || {}
      const orientationKey =
        Number(orientation) === 0 ? 'portrait' : 'horizontal'
      storedThumb[`${orientationKey}Dirty`] = true
      scene.thumbnail = storedThumb
      state.sceneThumbnail = {
        ...state.sceneThumbnail,
        ...{ [sceneId]: scene },
      }
    },
    clearSceneThumbnail(state, value) {
      state.sceneThumbnail = {}
    },
    clearNodesLayout(state, value) {
      state.nodesLayout = {}
    },
    updateIsCapturing(state, value) {
      state.isCapturing = value
    },
    updateSceneEditType(state, value) {
      state.sceneEditType = value
    },
    removeThumbnail(state, value) {
      value.forEach((sceneId) => {
        Vue.delete(state.sceneThumbnail, sceneId)
      })
    },
    removeUiLayout(state, value) {
      value.forEach((nodeId) => {
        Vue.delete(state.nodesLayout, nodeId)
      })
    },
    updateSceneThumbnail(state, value) {
      const { node, thumbnail, orientation } = value
      if (node) {
        const uuid = node.uuid
        const scene = state.sceneThumbnail[uuid] || {}
        const storedThumb = scene.thumbnail || {}
        const orientationKey =
          Number(orientation) === 0 ? 'horizontal' : 'portrait'
        storedThumb[orientationKey] = thumbnail
        storedThumb[`${orientationKey}Dirty`] = false
        scene.thumbnail = storedThumb
        // eslint-disable-next-line prettier/prettier
        state.sceneThumbnail = { ...state.sceneThumbnail, ...{ [uuid]: scene }}
      }
    },
    addSceneThumbnailItem(state, value) {
      state.sceneThumbnail = { ...state.sceneThumbnail, ...value }
    },
    addSceneUIPositionItem(state, value) {
      state.nodesLayout = { ...state.nodesLayout, ...value }
    },
    updateUIPosition(state, value) {
      const { node: sceneNode, orientation } = value
      const orientationKey = Number(orientation) === 0 ? 'ldef' : 'pdef'
      const children = getDescendants(sceneNode)
      children.forEach((child) => {
        if (child) {
          const uuid = child.uuid
          if (!uuid) return
          const layout = state.nodesLayout[uuid] || {}
          const { transformX, transformY } = transformXyLogic2Ui(child)
          layout[orientationKey] = {
            x: transformX,
            y: transformY,
            id: uuid,
            sceneId: sceneNode.uuid,
          }
          layout.sceneId = sceneNode.uuid
          // eslint-disable-next-line object-curly-spacing
          state.nodesLayout = { ...state.nodesLayout, ...{ [uuid]: layout } }
        }
      })
    },
    updateSceneList(state, value) {
      if (value) {
        state.sceneList = value
      } else {
        state.sceneList = gameIframe.gamePlay.children
      }
    },
    updateCurrentScene(state, node) {
      state.currentSceneNode = node
    },
  },

  actions: {
    // 图层树节点通过node 获取真实场景树节点
    getSceneTreeSourceNode({ rootState, state, commit }, { node }) {
      const trees = _.get(state, 'currentSceneNode') || {}
      return getNodeByUUID({
        item: trees,
        id: node.uuid,
      })
    },

    async switchBackNormalScene({ rootState, state, dispatch, commit }) {
      const normalScene = state.sceneList.filter((scene) =>
        isNormalScene(scene),
      )
      dispatch('workspace/game/clearActiveNode', '', {
        root: true,
      })
      await dispatch(
        'workspace/sceneManager/switchScene',
        { uuid: normalScene[0].uuid },
        {
          root: true,
        },
      )
      EventBus.$emit(Event.CAPTUREGAME, true)
      commit('workspace/resourceManager/closeDialog', '', {
        root: true,
      })
      dispatch('workspace/rightPane/switchToStageConfigTab', '', {
        root: true,
      })
    },

    togglePlayUnitUUID({ rootState, state, commit }, { node, uuid }) {
      const sourceNode = nodePool.find(node.uuid)
      const sceneConfig = sceneConfigScript.getScript(sourceNode)
      if (sourceNode && sceneConfig) {
        if (sceneConfig.playUUID === uuid) {
          // 移除已绑定玩法场景
          sceneConfig.playUUID = ''
        } else {
          // 绑定玩法场景
          sceneConfig.playUUID = uuid
        }
      }
    },

    resetState({ commit }) {
      commit('updateSceneList', [])
    },
    // 记录场景的截图，以及节点的位置信息
    updateSceneProperty({ commit }, value) {
      commit('updateSceneThumbnail', value)
      commit('updateUIPosition', value)
    },
    markSceneThumbnailDirty(
      { rootState, state, commit },
      { sceneId, orientation },
    ) {
      orientation = orientation || rootState.workspace.game.device.orientation
      sceneId = sceneId || state.currentSceneNode.uuid
      commit('markDirty', { orientation, sceneId })
    },
    ...actions,
  },
}

export default sceneManager
